import { Ban, CheckCircle2, Loader2, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { previewChanges, queueChanges, type DraftChange, type Preview } from '@/lib/authoring-api';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * PODGLĄD I ZAKOLEJKOWANIE PACZKI ZMIAN CONFIG-DOWN — wspólny dla zakładek „Zmiany" i „Zapytania".
 *
 * ⚠️ WYDZIELONY Z `pages/Authoring.tsx` (28.09.2026), BEZ ZMIAN W TREŚCI. Edycja w siatce wyniku
 * (etap 2 „jak SSMS") ma iść TĄ SAMĄ drogą co ręczny edytor: werdykt bramki dla każdej zmiany,
 * ostrzeżenia do potwierdzenia, uzasadnienie i odcisk podglądu. Druga kopia tego okna rozjechałaby
 * się z pierwszą przy pierwszej zmianie reguł.
 */
// ─────────────────────────── podgląd i zakolejkowanie ────────────────────────

export function PodgladZmian({
  groupId,
  changes,
  onClose,
  onQueued,
}: {
  groupId: string;
  changes: DraftChange[];
  onClose: () => void;
  /** Numer zakolejkowanej operacji — „Działania na bazie" pokazują po nim stan na węzłach. */
  onQueued: (opId: string) => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acked, setAcked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    previewChanges(groupId, changes)
      .then((p) => alive && setPreview(p))
      .catch((err) => alive && setError(err instanceof ApiError ? err.message : t('common.error')))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [groupId, changes, t]);

  const allAcked = (preview?.requiresAck ?? []).every((c) => acked.has(c));
  const canQueue = preview !== null && preview.blocked === 0 && allAcked && note.trim().length >= 10 && !saving;

  const queue = async () => {
    if (!preview) return;
    setSaving(true);
    setError(null);
    try {
      const { opId } = await queueChanges({
        groupId,
        changes,
        authorNote: note.trim(),
        previewToken: preview.token,
        acknowledged: [...acked],
      });
      toast.success(t('cd.queued'));
      onQueued(opId);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={t('cd.previewTitle')}
        description={t('cd.previewDesc').replace('{n}', String(changes.length))}
        className="w-[min(64rem,calc(100vw-2rem))]"
      >
        {loading ? (
          <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('cd.previewRunning')}
          </p>
        ) : (
          <>
            <div className="max-h-[24rem] space-y-3 overflow-y-auto">
              {preview?.verdicts.map((v) => (
                <div
                  key={v.index}
                  className={cn(
                    'rounded-md border p-3 text-sm',
                    v.ok ? 'border-border' : 'border-destructive/40 bg-destructive/5',
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={v.ok ? 'success' : 'destructive'}>
                      {v.ok ? <CheckCircle2 className="size-3" /> : <Ban className="size-3" />}
                      {v.ok ? t('cd.verdictOk') : t('cd.verdictBlocked')}
                    </Badge>
                    <span className="font-mono text-xs">
                      #{v.index + 1} {v.tableName} {v.action}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {Object.entries(v.pk)
                        .map(([k, val]) => `${k}=${val}`)
                        .join(', ')}
                    </span>
                  </div>

                  {v.payload && Object.keys(v.payload).length > 0 && (
                    <div className="mt-2 grid gap-1 text-xs">
                      {Object.entries(v.payload).map(([col, val]) => {
                        const before = v.current?.[col];
                        return (
                          <div key={col} className="flex flex-wrap items-baseline gap-2">
                            <span className="font-mono text-muted-foreground">{col}</span>
                            {before !== undefined && before !== null && String(before) !== String(val) && (
                              <>
                                <span className="text-muted-foreground line-through">{String(before)}</span>
                                <span aria-hidden>→</span>
                              </>
                            )}
                            <span className="font-medium">{String(val ?? 'NULL')}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {v.errors.map((e) => (
                    <p key={e} className="mt-2 flex items-start gap-1.5 text-xs text-destructive">
                      <Ban className="mt-0.5 size-3 shrink-0" />
                      {e}
                    </p>
                  ))}
                  {v.warnings.map((w) => (
                    <p key={w.code} className="mt-2 flex items-start gap-1.5 text-xs text-[var(--warning)]">
                      <TriangleAlert className="mt-0.5 size-3 shrink-0" />
                      {w.message}
                    </p>
                  ))}
                </div>
              ))}
            </div>

            <p className="mt-3 text-xs text-muted-foreground">{t('cd.previewIsPortalOnly')}</p>

            {(preview?.requiresAck.length ?? 0) > 0 && (
              <div className="mt-3 space-y-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3">
                <p className="text-xs font-medium">{t('cd.ackTitle')}</p>
                {preview?.requiresAck.map((code) => (
                  <label key={code} className="flex items-start gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={acked.has(code)}
                      onChange={(e) =>
                        setAcked((s) => {
                          const next = new Set(s);
                          if (e.target.checked) next.add(code);
                          else next.delete(code);
                          return next;
                        })
                      }
                    />
                    <span>{t(`cd.ack.${code}`)}</span>
                  </label>
                ))}
              </div>
            )}

            <FormGrid>
              <Field label={t('cd.authorNote')} htmlFor="note" required hint={t('cd.authorNoteHint')}>
                <Input
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('cd.authorNotePlaceholder')}
                  autoComplete="off"
                />
              </Field>
              <ModalError>{error}</ModalError>
            </FormGrid>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={!canQueue} onClick={() => void queue()}>
            {saving && <Loader2 className="animate-spin" />}
            {t('cd.queueAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
